import * as THREE from 'three';

// Simplex Noise 2D Implementation
// Based on Stefan Gustavson's implementation
class SimplexNoise {
  constructor(seed = Math.random()) {
    this.grad3 = [
      [1, 1, 0], [-1, 1, 0], [1, -1, 0], [-1, -1, 0],
      [1, 0, 1], [-1, 0, 1], [1, 0, -1], [-1, 0, -1],
      [0, 1, 1], [0, -1, 1], [0, 1, -1], [0, -1, -1]
    ];

    this.p = [];
    for (let i = 0; i < 256; i++) {
      this.p[i] = Math.floor(this.random(seed + i) * 256);
    }

    this.perm = [];
    for (let i = 0; i < 512; i++) {
      this.perm[i] = this.p[i & 255];
    }
  }

  random(seed) {
    const x = Math.sin(seed) * 10000;
    return x - Math.floor(x);
  }

  dot(g, x, y) {
    return g[0] * x + g[1] * y;
  }

  noise2D(xin, yin) {
    const F2 = 0.5 * (Math.sqrt(3.0) - 1.0);
    const G2 = (3.0 - Math.sqrt(3.0)) / 6.0;

    let n0, n1, n2;

    const s = (xin + yin) * F2;
    const i = Math.floor(xin + s);
    const j = Math.floor(yin + s);
    const t = (i + j) * G2;
    const X0 = i - t;
    const Y0 = j - t;
    const x0 = xin - X0;
    const y0 = yin - Y0;

    let i1, j1;
    if (x0 > y0) {
      i1 = 1;
      j1 = 0;
    } else {
      i1 = 0;
      j1 = 1;
    }

    const x1 = x0 - i1 + G2;
    const y1 = y0 - j1 + G2;
    const x2 = x0 - 1.0 + 2.0 * G2;
    const y2 = y0 - 1.0 + 2.0 * G2;

    const ii = i & 255;
    const jj = j & 255;
    const gi0 = this.perm[ii + this.perm[jj]] % 12;
    const gi1 = this.perm[ii + i1 + this.perm[jj + j1]] % 12;
    const gi2 = this.perm[ii + 1 + this.perm[jj + 1]] % 12;

    let t0 = 0.5 - x0 * x0 - y0 * y0;
    if (t0 < 0) {
      n0 = 0.0;
    } else {
      t0 *= t0;
      n0 = t0 * t0 * this.dot(this.grad3[gi0], x0, y0);
    }

    let t1 = 0.5 - x1 * x1 - y1 * y1;
    if (t1 < 0) {
      n1 = 0.0;
    } else {
      t1 *= t1;
      n1 = t1 * t1 * this.dot(this.grad3[gi1], x1, y1);
    }

    let t2 = 0.5 - x2 * x2 - y2 * y2;
    if (t2 < 0) {
      n2 = 0.0;
    } else {
      t2 *= t2;
      n2 = t2 * t2 * this.dot(this.grad3[gi2], x2, y2);
    }

    return 70.0 * (n0 + n1 + n2);
  }
}

// Generate multi-octave noise value
function multiOctaveNoise(noise, x, y, octaves, persistence, lacunarity) {
  let value = 0;
  let amplitude = 1;
  let frequency = 1;
  let maxValue = 0;

  for (let i = 0; i < octaves; i++) {
    value += noise.noise2D(x * frequency, y * frequency) * amplitude;
    maxValue += amplitude;
    amplitude *= persistence;
    frequency *= lacunarity;
  }

  return value / maxValue;
}

// Apply multi-octave noise displacement to geometry
function applyMultiOctaveNoise(geometry, params) {
  const {
    radius = 40,
    heightScale = 5.0,
    baseHeight = 0.0,
    noiseScale = 0.05,
    octaves = 3,
    persistence = 0.5,
    lacunarity = 2.0,
    falloffStart = 0.6,
    falloffPower = 3.0,
    seed = Math.random()
  } = params;

  const noise = new SimplexNoise(seed);
  const position = geometry.attributes.position;
  const heightData = [];

  for (let i = 0; i < position.count; i++) {
    const x = position.getX(i);
    const y = position.getY(i);

    // Calculate distance from center (normalized to radius)
    const distFromCenter = Math.sqrt(x * x + y * y) / radius;

    // Circular falloff for island shape
    let falloff = 1.0;
    if (distFromCenter > falloffStart) {
      const falloffDist = (distFromCenter - falloffStart) / (1.0 - falloffStart);
      falloff = Math.pow(1.0 - Math.min(falloffDist, 1.0), falloffPower);
    }

    // Generate multi-octave noise
    const noiseValue = multiOctaveNoise(
      noise,
      x * noiseScale,
      y * noiseScale,
      octaves,
      persistence,
      lacunarity
    );

    // Apply height with falloff (Z becomes height before rotation to horizontal)
    // Base height tapers with falloff so center is raised but edges still go underwater
    const heightOffset = baseHeight * falloff;

    // Subtract distance-based offset to push outer edges below water
    const edgeOffset = Math.max(0, (distFromCenter - falloffStart) * 8.0);
    const height = heightOffset + noiseValue * heightScale * falloff - edgeOffset;
    position.setZ(i, height);
    heightData.push(height);
  }

  position.needsUpdate = true;
  geometry.computeVertexNormals();

  // Store height data for AO generation
  geometry.userData.heightData = heightData;

  return geometry;
}

// Generate procedural normal map for fine sand detail
function generateSandNormalMap(size = 256) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  const imageData = ctx.createImageData(size, size);

  const noise = new SimplexNoise(Math.random());
  const scale = 0.1; // Fine-scale bumps
  const strength = 0.5;

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      // Sample noise at current position and neighbors for gradient
      const h0 = noise.noise2D(x * scale, y * scale);
      const h1 = noise.noise2D((x + 1) * scale, y * scale);
      const h2 = noise.noise2D(x * scale, (y + 1) * scale);

      // Calculate gradients
      const dx = (h1 - h0) * strength;
      const dy = (h2 - h0) * strength;

      // Convert to normal vector (stored as RGB)
      const normal = new THREE.Vector3(-dx, -dy, 1.0).normalize();

      const idx = (y * size + x) * 4;
      imageData.data[idx] = (normal.x * 0.5 + 0.5) * 255;     // R
      imageData.data[idx + 1] = (normal.y * 0.5 + 0.5) * 255; // G
      imageData.data[idx + 2] = (normal.z * 0.5 + 0.5) * 255; // B
      imageData.data[idx + 3] = 255;                          // A
    }
  }

  ctx.putImageData(imageData, 0, 0);

  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(4, 4); // Tile the normal map for detail
  texture.generateMipmaps = false;
  texture.minFilter = THREE.LinearFilter;

  return texture;
}

// Generate AO map from height data (darker in valleys, lighter on peaks)
function generateSandAOMap(geometry, size = 256) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  const imageData = ctx.createImageData(size, size);

  const heightData = geometry.userData.heightData || [];
  const segments = Math.sqrt(heightData.length) - 1;

  // Find height range for normalization (loop avoids call stack limits on large arrays)
  let minHeight = Infinity;
  let maxHeight = -Infinity;
  for (let i = 0; i < heightData.length; i++) {
    if (heightData[i] < minHeight) minHeight = heightData[i];
    if (heightData[i] > maxHeight) maxHeight = heightData[i];
  }
  const heightRange = maxHeight - minHeight;

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      // Map pixel to geometry vertex
      const gx = Math.floor((x / size) * segments);
      const gy = Math.floor((y / size) * segments);
      const idx2d = gy * (segments + 1) + gx;

      // Get height and normalize (0 = valley, 1 = peak)
      const height = heightData[idx2d] || 0;
      const normalizedHeight = (height - minHeight) / (heightRange || 1);

      // AO: darker in valleys (0.4), lighter on peaks (1.0)
      const ao = 0.4 + normalizedHeight * 0.6;

      const idx = (y * size + x) * 4;
      const value = ao * 255;
      imageData.data[idx] = value;     // R
      imageData.data[idx + 1] = value; // G
      imageData.data[idx + 2] = value; // B
      imageData.data[idx + 3] = 255;   // A
    }
  }

  ctx.putImageData(imageData, 0, 0);

  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.generateMipmaps = false;
  texture.minFilter = THREE.LinearFilter;

  return texture;
}

// Create PBR sand material with procedural maps
function createSandMaterial(geometry) {
  const normalMap = generateSandNormalMap();
  const aoMap = generateSandAOMap(geometry);

  // Need UV2 for AO map
  if (!geometry.attributes.uv2) {
    geometry.setAttribute('uv2', geometry.attributes.uv);
  }

  return new THREE.MeshStandardMaterial({
    color: 0xf4d03f,        // Golden yellow sand
    roughness: 0.9,         // Matte sand surface
    metalness: 0.0,         // Sand is not metallic
    normalMap: normalMap,
    normalScale: new THREE.Vector2(1.0, 1.0),
    aoMap: aoMap,
    aoMapIntensity: 0.5
  });
}

// Main export: create complete procedural sand mesh
export function createProceduralSand(params = {}) {
  const {
    segments = 128,
    size = 80
  } = params;

  // Create plane geometry
  const geometry = new THREE.PlaneGeometry(size, size, segments, segments);

  // Apply multi-octave noise displacement
  applyMultiOctaveNoise(geometry, params);

  // Rotate to horizontal (planes start vertical in Three.js)
  geometry.rotateX(-Math.PI / 2);

  // Create PBR material with procedural maps
  const material = createSandMaterial(geometry);

  // Create mesh
  const mesh = new THREE.Mesh(geometry, material);
  mesh.receiveShadow = true;
  mesh.castShadow = false;
  mesh.position.set(0, 1.5, 0); // Match current sand position

  return mesh;
}
